import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "@/services/studio/client";
import { useSkillsRequestContext } from "@/services/skills";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import type {
  DropdownResponse,
  Entity360Response,
  EntitySearchResponse,
  OrgListResponse,
  Review,
  ReviewSummary,
  SurfaceListResponse,
} from "@/services/studio/types";

export function useDropdown(
  toolName: string | undefined,
  args: Record<string, string> = {},
  enabled: boolean = true
) {
  const ctx = useSkillsRequestContext();
  const params = new URLSearchParams(args);
  const search = params.toString();
  return useQuery<DropdownResponse>({
    queryKey: ["dropdown", toolName, search, ctx.orgId, ctx.userId],
    queryFn: () =>
      apiGet<DropdownResponse>(
        `/api/dropdown/${encodeURIComponent(toolName!)}${search ? `?${search}` : ""}`
      ),
    enabled: Boolean(toolName && ctx.orgId) && enabled,
    // Dropdown options are backed by cheap live org queries (e.g.
    // /api/dropdown/list_org_users -> live SOQL). Caching them for a minute
    // meant a record created in Salesforce after the picker mounted never
    // showed up. Always refetch on mount, and let a window refocus refresh
    // them too; the short staleTime keeps rapid refocus/reconnect churn from
    // fanning out extra requests.
    staleTime: 5_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useOrgs() {
  const organizations = useAuthStoreV1((state) => state.organizations);
  const orgs = useMemo(
    () =>
      organizations.map((org) => {
        const metadataCounts = Array.isArray(org.metadataTypeCounts) ? org.metadataTypeCounts : [];
        const metadataCount = metadataCounts.reduce((sum, item) => sum + Number(item.count || 0), 0);
        const latest = org.latestMetadataRetrieval;
        return {
          id: org.id,
          label: org.orgName || org.salesforceOrgId || org.id,
          org_name: org.orgName || null,
          salesforce_org_id: org.salesforceOrgId || null,
          instance_url: org.instanceUrl || null,
          org_type: org.orgType || null,
          retrieval_id: latest?.id ?? null,
          retrieval_status: latest?.status ?? null,
          progress_percentage: latest?.progressPercentage ?? null,
          completed_at: null,
          metadata_count: metadataCount,
          has_live_oauth: Boolean(org.instanceUrl),
        };
      }),
    [organizations]
  );
  return useQuery<OrgListResponse>({
    queryKey: ["orgs", orgs.map((org) => org.id).join("|")],
    queryFn: async () => ({ orgs }),
    initialData: { orgs },
    staleTime: 30_000,
    retry: false,
  });
}

export function useEntitySearch(query: string) {
  const ctx = useSkillsRequestContext();
  const q = query.trim();
  return useQuery<EntitySearchResponse>({
    queryKey: ["entities", "search", q, ctx.orgId, ctx.userId],
    queryFn: () =>
      apiGet<EntitySearchResponse>(
        `/api/entities/search${q ? `?q=${encodeURIComponent(q)}` : ""}`
      ),
    enabled: Boolean(ctx.orgId),
    staleTime: 30_000,
    retry: false,
  });
}

export function useEntity360(entityType: string, name: string | undefined) {
  const ctx = useSkillsRequestContext();
  return useQuery<Entity360Response>({
    queryKey: ["entities", "360", entityType, name ?? "", ctx.orgId, ctx.userId],
    queryFn: () => {
      if (entityType === "org") {
        return apiGet<Entity360Response>("/api/entities/org/360");
      }
      return apiGet<Entity360Response>(
        `/api/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(name ?? "")}/360`
      );
    },
    enabled: Boolean(ctx.orgId) && (entityType === "org" || Boolean(name)),
    staleTime: 30_000,
    retry: false,
  });
}

export function useSurfaces() {
  const ctx = useSkillsRequestContext();
  return useQuery<SurfaceListResponse>({
    queryKey: ["surfaces", ctx.orgId, ctx.userId],
    queryFn: () => apiGet<SurfaceListResponse>("/api/surfaces"),
    enabled: Boolean(ctx.orgId),
    staleTime: 60_000,
    retry: false,
  });
}

export function useSkillReviews(skill_id: string | undefined) {
  return useQuery<ReviewSummary>({
    queryKey: ["skill-reviews", skill_id],
    queryFn: async () => ({
      skill_id: skill_id!,
      avg_stars: 0,
      count: 0,
      histogram: {},
      recent: [],
    }),
    enabled: Boolean(skill_id),
    staleTime: 30_000,
    retry: false,
  });
}

export function useMyReview(skill_id: string | undefined) {
  return useQuery<Review | null>({
    queryKey: ["my-review", skill_id],
    queryFn: async () => null,
    enabled: Boolean(skill_id),
    staleTime: 30_000,
    retry: false,
  });
}

export interface SubmitReviewVars {
  skill_id: string;
  stars: number;
  comment?: string | null;
}

export function useSubmitReview() {
  const qc = useQueryClient();
  return useMutation<Review, Error, SubmitReviewVars>({
    mutationFn: async ({ skill_id, stars, comment }) => ({
      id: `local-${Date.now()}`,
      skill_id,
      stars,
      comment: comment ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user_id: "local",
    }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["skill-reviews", vars.skill_id] });
      qc.invalidateQueries({ queryKey: ["my-review", vars.skill_id] });
      qc.invalidateQueries({ queryKey: ["skills"] });
      qc.invalidateQueries({ queryKey: ["skill", vars.skill_id] });
    },
  });
}

export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation<void, Error, { skill_id: string }>({
    mutationFn: async () => undefined,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["skill-reviews", vars.skill_id] });
      qc.invalidateQueries({ queryKey: ["my-review", vars.skill_id] });
      qc.invalidateQueries({ queryKey: ["skills"] });
      qc.invalidateQueries({ queryKey: ["skill", vars.skill_id] });
    },
  });
}
